import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUniversityProgress } from '@/hooks/useUniversityProgress';
import QuizResultsTable from '@/components/education/QuizResultsTable';
import SEO from '@/components/common/SEO';

const M = '#06060c';
const G = '#d4a843';
const IN = '#0ea5e9';
const BR = 'rgba(255,255,255,0.07)';
const CA = 'rgba(255,255,255,0.04)';
const TX = 'rgba(255,255,255,0.88)';
const MU = 'rgba(255,255,255,0.45)';

interface DegreeProgress {
  class_id: string;
  name: string;
  mastery_score: number;
  last_active_at: string | null;
  total_lessons: number;
  completed_lessons: number;
  module_plan: {
    module_id: number;
    title: string;
    certificate: string;
    lessons: { lesson_id: number }[];
  }[];
  progress_state: {
    current_unit: number;
    current_lesson: number;
    total_lessons_completed: number;
  } | null;
}

function getMasteryColor(s: number) {
  if (s >= 80) return '#4ade80';
  if (s >= 60) return G;
  return '#f87171';
}

function RadialProgress({ pct, size = 80 }: { pct: number; size?: number }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={6} />
      <motion.circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={pct >= 80 ? '#4ade80' : pct >= 40 ? G : IN}
        strokeWidth={6}
        strokeLinecap="round"
        strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: circ - dash }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
    </svg>
  );
}

export default function UniversityProgressPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, isLoading: authLoading } = useAuth();

  const [degrees, setDegrees] = useState<DegreeProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [isMobile, setIsMobile] = useState(false);
  const [showQuizHistory, setShowQuizHistory] = useState(false);

  const { quizResults, degreeNames, totalQuizzes, averageScore } = useUniversityProgress();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 820);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/signin', { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const { data: enrolments } = await supabase
          .from('class_enrollments')
          .select(`
            class_id, mastery_score, last_active_at, progress_state,
            classes (id, name, module_plan, facility_id)
          `)
          .eq('student_id', user!.id);

        if (cancelled) return;

        const uni = (enrolments || []).filter(e => {
          const cls = Array.isArray(e.classes) ? e.classes[0] : e.classes;
          return (cls as any)?.facility_id === '00000000-0000-0000-0000-000000000002';
        });

        const mapped: DegreeProgress[] = uni.map(e => {
          const cls = (Array.isArray(e.classes) ? e.classes[0] : e.classes) as any;
          const mp = cls?.module_plan || [];
          const total = mp.reduce((s: number, m: any) => s + (m.lessons?.length || 0), 0);
          const ps = e.progress_state as DegreeProgress['progress_state'];
          return {
            class_id: e.class_id,
            name: cls?.name || '',
            mastery_score: Number(e.mastery_score) || 0,
            last_active_at: e.last_active_at,
            total_lessons: total,
            completed_lessons: ps?.total_lessons_completed || 0,
            module_plan: mp,
            progress_state: ps,
          };
        });

        setDegrees(mapped);
      } catch (err) {
        console.error('ProgressPage load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [user]);

  const filtered = degrees.filter(d => {
    if (filter === 'active') return d.completed_lessons > 0 && d.completed_lessons < d.total_lessons;
    if (filter === 'completed') return d.completed_lessons >= d.total_lessons && d.total_lessons > 0;
    return true;
  });

  const totalLessons = degrees.reduce((s, d) => s + d.completed_lessons, 0);
  const overallAvg = degrees.length
    ? Math.round(degrees.reduce((s, d) => s + d.mastery_score, 0) / degrees.length)
    : 0;
  const completedDegrees = degrees.filter(d => d.completed_lessons >= d.total_lessons && d.total_lessons > 0).length;

  if (authLoading || loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: M }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'linear' }}
          style={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid rgba(212,168,67,0.15)`, borderTopColor: G }}
        />
      </div>
    );
  }

  return (
    <div style={{ background: M, minHeight: '100vh', color: TX, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <SEO title="Progress" noindex />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '32px 16px 80px' : '40px 24px 80px' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              color: MU,
              cursor: 'pointer',
              fontSize: 13,
              marginBottom: 16,
              padding: 0,
              fontWeight: 600
            }}
          >
            {t('university.progress.backToDashboard', '← BACK TO DASHBOARD')}
          </button>
          <p style={{ fontSize: 11, color: G, fontWeight: 800, letterSpacing: '2.4px', marginBottom: 8, textTransform: 'uppercase' }}>
            {t('university.progress.eyebrow', 'STILL WATERS UNIVERSITY')}
          </p>
          <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 900, margin: '0 0 32px', letterSpacing: '-0.8px', lineHeight: 1.1 }}>
            {t('university.progress.title', 'Your Learning Progress')}
          </h1>
        </motion.div>

        {/* Overview stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: isMobile ? 12 : 20,
          marginBottom: isMobile ? 32 : 48
        }}>
          {[
            { label: t('university.progress.statsDegrees', 'Degrees Enrolled'), value: degrees.length, icon: '🎓', color: IN },
            { label: t('university.progress.statsCompleted', 'Degrees Completed'), value: completedDegrees, icon: '✅', color: '#4ade80' },
            { label: t('university.progress.statsLessons', 'Lessons Done'), value: totalLessons, icon: '📖', color: G },
            { label: t('university.progress.statsMastery', 'Overall Mastery'), value: `${overallAvg}%`, icon: '🎯', color: IN },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              whileHover={!isMobile ? { y: -5, boxShadow: '0 12px 24px rgba(0,0,0,0.2)' } : {}}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 20, padding: isMobile ? '20px' : '24px', transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: isMobile ? 22 : 26, marginBottom: 16 }}>{s.icon}</div>
              <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 900, color: s.color, letterSpacing: '-1px', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: MU, marginTop: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Overall progress arc */}
        {degrees.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              background: 'linear-gradient(160deg, rgba(14,165,233,0.08) 0%, rgba(212,168,67,0.05) 100%)',
              border: `1.5px solid ${BR}`, borderRadius: 24,
              padding: isMobile ? '24px' : '40px', marginBottom: 48,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center',
              gap: isMobile ? 32 : 48,
            }}
          >
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <RadialProgress pct={overallAvg} size={isMobile ? 130 : 150} />
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}>
                <div style={{ fontWeight: 900, fontSize: isMobile ? 24 : 32, color: TX, lineHeight: 1 }}>{overallAvg}%</div>
                <div style={{ fontSize: 11, color: MU, fontWeight: 700, textTransform: 'uppercase', marginTop: 4 }}>Mastery</div>
              </div>
            </div>
            <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
              <h2 style={{ fontWeight: 800, fontSize: isMobile ? 20 : 28, margin: '0 0 12px', letterSpacing: '-0.5px' }}>
                {t('university.progress.standingTitle', 'Academic Standing')}
              </h2>
              <p style={{ color: MU, fontSize: 15, lineHeight: 1.7, margin: '0 0 24px', fontWeight: 500 }}>
                {overallAvg >= 80
                  ? 'Your academic performance is exceptional. You are demonstrating deep mastery of the core theological concepts.'
                  : overallAvg >= 60
                    ? 'You are maintaining a strong academic standing. Focus on consistent reviewing to perfect your understanding.'
                    : 'You\'ve started your journey. Consistency is the key to spiritual and academic growth. Keep pushing.'}
              </p>
              <div style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                {degrees.slice(0, 3).map(d => (
                  <div key={d.class_id} style={{
                    background: 'rgba(255,255,255,0.04)', border: `1px solid ${BR}`,
                    borderRadius: 10, padding: '8px 16px', fontSize: 12, fontWeight: 600
                  }}>
                    <span style={{ color: getMasteryColor(d.mastery_score), fontWeight: 800 }}>{d.mastery_score}%</span>
                    <span style={{ color: TX, marginLeft: 8 }}>{d.name.split(' ').slice(0, 2).join(' ')}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 4, border: `1px solid ${BR}`, width: 'fit-content' }}>
          {(['all', 'active', 'completed'] as const).map(f => (
            <motion.button
              key={f}
              whileHover={{ scale: 1.02 }}
              onClick={() => setFilter(f)}
              style={{
                background: filter === f ? G : 'transparent',
                color: filter === f ? '#06060c' : MU,
                border: 'none', padding: '8px 20px', borderRadius: 8,
                cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.15s',
              }}
            >
              {f === 'all' ? t('university.progress.filterAll', 'All')
                : f === 'active' ? t('university.progress.filterActive', 'In Progress')
                  : t('university.progress.filterDone', 'Completed')}
            </motion.button>
          ))}
        </div>

        {/* Degree progress cards */}
        {filtered.length === 0 ? (
          <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 20, padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 16 }}>📚</div>
            <p style={{ color: MU, fontSize: 15 }}>
              {t('university.progress.empty', 'No degrees match this filter.')}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {filtered.map((d, i) => {
              const pct = d.total_lessons > 0
                ? Math.min(Math.round((d.completed_lessons / d.total_lessons) * 100), 100)
                : 0;

              return (
                <motion.div
                  key={d.class_id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  whileHover={{ borderColor: 'rgba(212,168,67,0.3)' }}
                  style={{
                    background: CA, border: `1px solid ${BR}`,
                    borderRadius: 18, padding: '24px 28px',
                    transition: 'border-color 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontWeight: 700, fontSize: 17, margin: '0 0 6px' }}>{d.name}</h3>
                      <p style={{ color: MU, fontSize: 13, margin: '0 0 16px' }}>
                        {d.completed_lessons}/{d.total_lessons} {t('university.progress.lessons', 'lessons')} ·
                        {d.last_active_at
                          ? ' ' + t('university.progress.lastActive', 'Last active {{date}}', {
                            date: new Date(d.last_active_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
                          })
                          : ' ' + t('university.progress.notStarted', 'Not started')}
                      </p>

                      {/* Progress bar */}
                      <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 100, height: 7, overflow: 'hidden', marginBottom: 8 }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 1, ease: 'easeOut', delay: i * 0.04 }}
                          style={{
                            height: '100%', borderRadius: 100,
                            background: `linear-gradient(90deg, ${IN}, ${G})`,
                          }}
                        />
                      </div>

                      {/* Module breakdown */}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                        {d.module_plan.map(mod => {
                          const modDone = mod.module_id < (d.progress_state?.current_unit || 1);
                          const modCurrent = mod.module_id === (d.progress_state?.current_unit || 1);
                          return (
                            <div key={mod.module_id} style={{
                              background: modDone ? 'rgba(74,222,128,0.1)' : modCurrent ? 'rgba(212,168,67,0.1)' : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${modDone ? 'rgba(74,222,128,0.3)' : modCurrent ? 'rgba(212,168,67,0.3)' : BR}`,
                              borderRadius: 8, padding: '4px 10px', fontSize: 11,
                              color: modDone ? '#4ade80' : modCurrent ? G : MU,
                            }}>
                              {modDone ? '✓ ' : modCurrent ? '▶ ' : ''}{mod.title.replace('Module ', 'M')}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 800, fontSize: 28, color: getMasteryColor(d.mastery_score), letterSpacing: '-1px' }}>
                          {d.mastery_score}%
                        </div>
                        <div style={{ fontSize: 11, color: MU }}>{t('university.progress.mastery', 'mastery')}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <motion.button
                          whileHover={{ scale: 1.04 }}
                          onClick={() => navigate(`/degree/${d.class_id}`)}
                          style={{
                            background: CA, border: `1px solid ${BR}`,
                            borderRadius: 9, padding: '8px 14px',
                            fontSize: 12, fontWeight: 600, cursor: 'pointer', color: TX,
                          }}
                        >
                          {t('university.progress.overview', 'Overview')}
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.04 }}
                          onClick={() => navigate(`/learn/${d.class_id}`)}
                          style={{
                            background: `linear-gradient(135deg, ${G}, #b8912e)`,
                            color: '#06060c', border: 'none',
                            borderRadius: 9, padding: '8px 16px',
                            fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          }}
                        >
                          {d.completed_lessons === 0
                            ? t('university.progress.start', 'Start')
                            : t('university.progress.continue', 'Continue')} →
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* ── Quiz History Section ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{ marginTop: isMobile ? 32 : 48 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontWeight: 800, fontSize: isMobile ? 20 : 24, margin: 0, letterSpacing: '-0.5px' }}>
                {t('university.progress.quizHistoryTitle', 'Quiz History')}
              </h2>
              <p style={{ color: MU, fontSize: 13, marginTop: 4, fontWeight: 500 }}>
                {t('university.progress.quizHistorySubtitle', '{{count}} quizzes · {{avg}}% average', { count: totalQuizzes, avg: averageScore })}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.04 }}
              onClick={() => setShowQuizHistory(s => !s)}
              style={{
                background: CA, border: `1px solid ${BR}`,
                borderRadius: 10, padding: '8px 16px',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', color: TX,
              }}
            >
              {showQuizHistory
                ? t('university.progress.hideQuizHistory', 'Hide')
                : t('university.progress.showQuizHistory', 'Show Details')}
            </motion.button>
          </div>

          {showQuizHistory && quizResults.length > 0 && (
            <QuizResultsTable results={quizResults} degreeNames={degreeNames} />
          )}

          {!showQuizHistory && quizResults.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
              gap: 12,
            }}>
              {quizResults.slice(0, 3).map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  style={{
                    background: CA, border: `1px solid ${BR}`,
                    borderRadius: 14, padding: '16px 20px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: MU, fontWeight: 600 }}>
                      M{r.lesson_unit} · L{r.lesson_number}
                    </span>
                    <span style={{
                      fontSize: 14, fontWeight: 800,
                      color: r.score_percentage >= 80 ? '#4ade80' : r.score_percentage >= 60 ? G : '#f87171',
                    }}>
                      {r.score_percentage}%
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: TX, fontWeight: 600, margin: '0 0 4px' }}>
                    {r.degree_name?.split(' ').slice(0, 3).join(' ')}
                  </p>
                  <p style={{ fontSize: 11, color: MU, margin: 0 }}>
                    {new Date(r.attempted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </p>
                </motion.div>
              ))}
            </div>
          )}

          {totalQuizzes === 0 && (
            <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 16, padding: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📝</div>
              <p style={{ color: MU, fontSize: 14 }}>
                {t('university.progress.noQuizzes', 'No quizzes completed yet. Your quiz results will appear here showing what you got right and wrong.')}
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
